import React, { useState, useRef, useEffect } from 'react';
import { feedbackMap } from '../utils/utils';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRange, RangeKeyDict } from 'react-date-range';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ConsoleLoggingListener } from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/src/common.browser/ConsoleLoggingListener';
import { Cohort, InterviewObject, OrganizationInfo, User } from '@/utils/types';
import Rectangle from './ui/rectangle';
import LinearProgressWithLabel from './ui/progress';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Loading from 'react-loading';

// interface AdminOrgTrendProps {
//   membersInfo: { [key: string]: { [key: string]: InterviewObject[] } };
//   organization: string;
// }

interface AdminOrgTrendProps {
  orgInfo: OrganizationInfo | null | undefined,
  cohorts: Cohort[],
  admin: User,
  navigateToMembers: () => void
}

interface FeedbackMapping {
  [key: string]: [string[], string[], string[]] | null;
}

interface ChartDataPoint {
  x: Date;
  y: number;
  obj: InterviewObject;
}

function AdminOrgTrend(props: AdminOrgTrendProps) {
  const totalLicenses: number = props.cohorts.reduce((acc, obj) => acc + (obj.totalLicenses || 0), 0);
  const licensesAssigned: number = props.cohorts.reduce((acc, obj) => acc + (obj.members.filter(x => x.membershipType === "student").length || 0), 0);

  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<RangeKeyDict>({
    selection: {
      startDate: undefined,
      endDate: new Date(),
      key: 'selection',
    }
  });
  const [criteriaFilter, setCriteriaFilter] = useState<string>('all');
  const [feedbackMapping, setFeedbackMapping] = useState<[FeedbackMapping, string[]]>([{}, []]);
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);
  const [dateAdjust, setDateAdjust] = useState<boolean>(false);

  const scores: { [key: string]: number } = {
    "Poor": 10,
    "Satisfactory": 50,
    "Good": 85,
    "Exceptional": 100
  };

  const typeMap: { [key: string]: string } = {
    'resume based': 'behavioral',
    'behavioral': 'behavioral',
    'estimation': 'technical',
    'strategy': 'technical',
    'system design': 'technical',
    'software concepts': 'technical',
    'coding': 'technical',
    'product design': 'technical',
  };

  // useEffect(() => {
  //   CreateInitMap(props.membersInfo);
  // }, [props.membersInfo]);

  const overlayRef = useRef<HTMLDivElement>(null);
  const data = getChartData();
  const stats = getStats();

  function getScore(timestamp: string): number {
    const feed = feedbackMapping[0][timestamp];
    if (feed === null || feed === undefined) {
      return 0;
    }
    let total = 0;
    let copy = criteriaFilter !== 'all' ? feed[1].filter((x, i) => feed[0][i] === criteriaFilter) : feed[1];
    for (let i = 0; i < copy.length; i++) {
      total += scores[copy[i]];
    }
    return total / copy.length;
  }

  async function CreateInitMap(infos: { [key: string]: { [key: string]: InterviewObject[] } }) {
    let dict: FeedbackMapping = {};
    let allCriteria: string[] = [];
    const all: InterviewObject[] = [];
    for (let memkey in infos) {
      for (let key in infos[memkey]) {
        all.push(...infos[memkey][key]);
      }
    }
    for (let i = 0; i < all.length; i++) {
      let x = all[i];
      if ((x["timestamp"] !== undefined)) {
        let f = await feedbackMap(x["feedback"]);
        dict[x["timestamp"]] = f;
        if (f !== null) {
          f[0].forEach(c => {
            if (!allCriteria.includes(c.toLowerCase())) {
              allCriteria.push(c.toLowerCase());
            }
          });
        }
      }
    }
    allCriteria.sort();
    setFeedbackMapping([dict, allCriteria]);
  }

  function getDate(date: string): Date | null {
    if (date !== undefined && date !== "" && date !== null) {
      const dateTimeArr = date.split(' ');
      const dateArr = dateTimeArr[0].split('-');
      const timeArr = dateTimeArr[1].split(':');
      return new Date(parseInt(dateArr[0]), parseInt(dateArr[1]) - 1, parseInt(dateArr[2]), parseInt(timeArr[0]), parseInt(timeArr[1]), parseInt(timeArr[2]));
    }
    return null;
  }



  function inDateRange(ans: InterviewObject): boolean {
    if (dateAdjust) {
      if (ans["timestamp"] === undefined) {
        return false;
      }
      const d = getDate(ans["timestamp"]);
      return d !== null && dateRange.selection.endDate !== undefined && dateRange.selection.startDate !== undefined && d <= dateRange.selection.endDate && d >= dateRange.selection.startDate;
    }
    return true;
  }

  function hasCriteria(ans: InterviewObject): boolean {
    if (criteriaFilter !== 'all') {
      const t = ans["timestamp"];
      if (t !== undefined) {
        let f = feedbackMapping[0][t];
        if (f !== null) {
          let list = f[0].filter(x => (x.toLowerCase() === criteriaFilter.toLowerCase()));
          return list.length > 0;
        }
      }
      return false;
    }
    return true;
  }

  function inTypeFilter(ans: InterviewObject): boolean {
    if (typeFilter !== 'all') {
      let val = typeMap[ans["specType"]];
      val = val === undefined ? 'other' : val;
      return val === typeFilter;
    }
    return true;
  }

  function applyFilters(all: InterviewObject[]): InterviewObject[] {
    return all.filter(ans => inDateRange(ans) && inTypeFilter(ans) && hasCriteria(ans));
  }

  function getChartData(): ChartDataPoint[] {
    const all: InterviewObject[] = [];
    // for (let memkey in props.membersInfo) {
    //   for (let key in props.membersInfo[memkey]) {
    //     all.push(...props.membersInfo[memkey][key]);
    //   }
    // }

    const allFiltered = applyFilters(all);
    const data = allFiltered.map(ans => {
      const date = getDate(ans["timestamp"]);
      return date ? { x: date, y: getScore(ans["timestamp"]), obj: ans } : null;
    }).filter((x): x is ChartDataPoint => x !== null && x.y !== 0);
    data.sort((a, b) => a.x.getTime() - b.x.getTime());
    return data;
  }

  function getDateRangeDisplay(): string {
    const start = dateRange.selection.startDate === undefined ? 'Beginning' : formatDate(dateRange.selection.startDate);
    const end = dateRange.selection.endDate ? formatDate(dateRange.selection.endDate) : '';
    return `${start} - ${end} `;
  }

  function formatDate(currentDate: Date): string {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return currentDate.toLocaleDateString('en-US', options);
  }

  function formatTime(currentDate: Date): string {
    return `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
  }

  function isOverlayClicked(e: React.MouseEvent): void {
    if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
      setPickerOpen(false);
    }
  }

  function calcDataAverage(): string {
    const sum = data.reduce((accumulator, currentValue) => accumulator + currentValue.y, 0);
    return (sum / data.length).toFixed(1);
  }

  function getStats(): [string, string, string] {
    const catDict: { [key: string]: { score: number, count: number } } = {};
    const roleDict: { [key: string]: number } = {};
    for (let i = 0; i < data.length; i++) {
      let role = data[i].obj["role"];
      let cat = data[i].obj["specType"];
      if (cat !== undefined && cat !== "" && catDict.hasOwnProperty(cat)) {
        const currCount = catDict[cat].count;
        const currScore = catDict[cat].score;
        catDict[cat] = { score: ((currScore * currCount) + data[i].y) / (currCount + 1), count: currCount + 1 };
      }
      else if (cat !== undefined && cat !== "") {
        catDict[cat] = { score: data[i].y, count: 1 };
      }
      if (role !== undefined && role !== "" && roleDict.hasOwnProperty(role)) {
        roleDict[role] += 1;
      }
      else if (role !== undefined && role !== "") {
        roleDict[role] = 1;
      }
    }
    const roleEntries = Object.entries(roleDict);
    roleEntries.sort((a, b) => a[1] - b[1]);
    const catEntries = Object.entries(catDict);
    catEntries.sort((a, b) => a[1].score - b[1].score);

    const roleMax = roleEntries.length > 0 ? roleEntries[roleEntries.length - 1][0] : "N/A";
    const catMax = catEntries.length > 0 ? catEntries[catEntries.length - 1][0] : "N/A";
    const catMin = catEntries.length > 0 ? catEntries[0][0] : "N/A";

    return [roleMax, catMax, catMin !== catMax ? catMin : "N/A"];
  }

  // return (
  //   <div className="flex-col w-[100%] items-start">
  //     <div className="mt-[8vh] flex-col items-start">
  //       <p className="font-bold text-black text-5xl">Welcome to the dashboard</p>
  //       <br/>
  //       <br/>
  //       <p className="text-md"><span>Take a look at what {props.organization} members are practicing </span><span></span></p>
  //     </div>

  //     <div className="flex-col w-[100%] mt-4">
  //       <div className="flex flex-start flex-wrap">
  //         {/* Fit three rectangles per row */}
  //         <Rectangle fit="four" 
  //         content={`${Object.keys(props.membersInfo).length}`}
  //         title={"Total Active Licenses"} 
  //           />
  //         <Rectangle fit="four" 
  //         content={`${data.length}`}
  //         title={"Total Interviews Practiced"}/>
  //         <Rectangle fit="four"
  //         content={`${calcDataAverage()}%`}
  //         title = {"Average Interview Score"} />
  //         <Rectangle fit="four"
  //         content={stats[0]}
  //         title={"Top Practiced Role"}/>
  //       </div>
  //     </div>
  //       {/* <div className="row">
  //         <div className="square">
  //           <h2>{Object.keys(props.membersInfo).length}</h2>
  //           <p>Members</p>
  //         </div>
  //         <div className="square">
  //           <h2>{data.length}</h2>
  //           <p>Interviews Practiced</p>
  //         </div>
  //         <div className="square">
  //           <h2>{calcDataAverage()}%</h2>
  //           <p>Average Interview Score</p>
  //         </div>
  //       </div>
  //       <div className="row">
  //         <div className="square">
  //           <h2>{stats[0]}</h2>
  //           <p>Top Practiced Role</p>
  //         </div> */}
  //         {/* <div className="square">
  //           <h2>{stats[1]}</h2>
  //           <p>Average Highest Scoring Category</p>
  //         </div>
  //         <div className="square">
  //           <h2>{stats[2]}</h2>
  //           <p>Average Lowest Scoring Category</p>
  //         </div> */}
  //       {/* </div>
  //     </div> */}

  //     {/* <div className="chartWrapper2">
  //       <div className='chartDiv'>
  //         <ResponsiveContainer width="90%" height="100%">
  //           <LineChart
  //             data={data.map(d => ({ x: `${formatDate(d.x)} (${formatTime(d.x)})`, y: d.y }))}
  //             margin={{
  //               top: 15,
  //               right: 30,
  //               left: 20,
  //               bottom: 5,
  //             }}
  //           >
  //             <XAxis dataKey="x" />
  //             <YAxis />
  //             <Tooltip />
  //             <Legend verticalAlign="top" />
  //             <Line type="monotone" name="Average Interview Score Percentage over time" dataKey="y" strokeWidth={3} stroke="#ff914d" activeDot={{ r: 8 }} />
  //           </LineChart>
  //         </ResponsiveContainer>
  //       </div>
  //     </div> */}
  //   </div>
  // );
  return (
    <div className="flex-col w-[100%] items-start">
      <div className="flex-col items-start">
        <p className="font-bold text-grayBlue text-5xl">Welcome back, {props.admin.name.split(' ')[0]}</p>
        <br />
        {props.orgInfo === undefined && props.orgInfo !== null && <div className="loading">
          <Loading
            type="bubbles"
            color="#000000"
            height={200}
            width={100}
          />
        </div>}
        {props.orgInfo === null && <p className="text-md"><span>Nothing to Show.</span><span></span></p>}
      </div>
      {props.orgInfo &&
        <div className="w-[100%]">
          <Box sx={{ width: '100%', boxShadow: '1', borderBottom: '1px solid rgba(0, 0, 0, 0.2)' }}>
            <Toolbar
              sx={[
                {
                  backgroundColor: "white",
                  pl: { sm: 2 },
                  pr: { xs: 1, sm: 1 },
                }
              ]}
            >
              {(
                <Typography
                  sx={{ flex: '1 1 100%', fontWeight: '450', textAlign: 'left' }}
                  variant="h6"
                  id="tableTitle"
                  component="div"
                >
                  Member Licenses
                </Typography>
              )}
            </Toolbar>
          </Box>
          <div className="flex-col shadow-sm mb-5 p-2 bg-white w-[100%]">
            <div className="flex justify-between items-center  w-[100%]">
              <div className="w-[65%] ml-2">
                <LinearProgressWithLabel title={`You have ${licensesAssigned} student ${licensesAssigned === 1 ? 'license' : 'licenses'} assigned.`} value={(licensesAssigned / totalLicenses) * 100} label={`${licensesAssigned}/${totalLicenses}`} />
              </div>
              <div className='px-3 py-2 text-white cursor-pointer hover:bg-grayBlue-hover font-[300] bg-grayBlue rounded-md shadow-sm' onClick={props.navigateToMembers}><p>Manage Members</p></div>
            </div>
            <p className="self-start text-left text-customBlack ml-3 mt-2">{`Your current plan allows for ${totalLicenses} student licenses. To add more licenses, upgrade your plan.`}</p>
          </div>
        </div>}
      {props.orgInfo &&
        <div className="w-[100%]">
          <Box sx={{ width: '100%', boxShadow: '1', borderBottom: '1px solid rgba(0, 0, 0, 0.2)' }}>
            <Toolbar
              sx={[
                {
                  backgroundColor: "white",
                  pl: { sm: 2 },
                  pr: { xs: 1, sm: 1 },
                }
              ]}
            >
              {(
                <Typography
                  sx={{ flex: '1 1 100%', fontWeight: '450', textAlign: 'left' }}
                  variant="h6"
                  id="tableTitle"
                  component="div"
                >
                  Quick Analytics
                </Typography>
              )}
            </Toolbar>
          </Box>
          <div className="flex-col shadow-sm mb-5 p-2 bg-white w-[100%]">
            <div className="flex-col w-[100%] my-4">
              <div className="flex justify-center flex-wrap">
                {/* Fit three rectangles per row */}
                <Rectangle fit="four"
                  content={`${props.orgInfo!.totalActiveLicenses}`}
                  title={"Total Active Licenses"}
                />
                <Rectangle fit="four"
                  content={`${props.orgInfo!.totalInterviewsPracticed}`}
                  title={"Total Interviews Practiced"} />
                <Rectangle fit="four"
                  content={`${(props.orgInfo!.avgInterviewScore).toFixed(1)}%`}
                  title={"Average Interview Score"} />
                <Rectangle fit="four"
                  content={props.orgInfo!.topPracticedRole}
                  title={"Top Practiced Role"} />
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  );
}

export default AdminOrgTrend;
